import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import toPairs from 'lodash/toPairs';
import { componentsCache } from '@atlassian/jira-cache';
import log from '@atlassian/jira-common-util-logging/src/log';
import { ValidationError } from '@atlassian/jira-fetch';
import type { FieldValidationError } from '@atlassian/jira-fetch/src/utils';
import type { ProjectKey } from '@atlassian/jira-shared-types';
import type { CreatedComponent } from '../model/types';
import type { ComponentResponse } from '../rest/types';

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (
	promise: Promise<Response>,
	transformer: (CRest: ComponentResponse) => CreatedComponent,
	projectKey: ProjectKey,
): Promise<CreatedComponent> =>
	promise
		.then((response) => Promise.all([response.ok, response.json()])) // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.then(([ok, json]: [any, any]) => {
			if (ok) {
				const component = transformer(json);

				if (component.id !== undefined) {
					const newComponent = {
						...component,
						projectKey,
					};

					try {
						componentsCache.set(component.id.toString(), newComponent);
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (error: any) {
						log.safeErrorWithoutCustomerData(
							'project-directories.components.cache.set',
							error.message,
						);
					}
				}

				return component;
			}

			const errors = pick(json.errors, ['name', 'componentLead']);
			if (!isEmpty(errors)) {
				const fieldErrors: FieldValidationError[] = toPairs(errors).map(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					([field, error]: [any, any]) => ({ field, error }),
				);
				throw new ValidationError('Validation error', fieldErrors);
			}

			throw new Error(
				`Request failed with a json response but cannot extract validation errors: ${JSON.stringify(
					json,
				)}`,
			);
		})
		.catch((e) => {
			throw e instanceof Error ? e : new Error(e);
		});
